import { z } from "zod";

/**
 * HTML form checkboxes (native or Radix) submit their `value` attribute
 * (default "on") when checked, and are absent from form data when unchecked.
 * Zod's `z.boolean()` rejects strings, so a naive `field: z.boolean()` schema
 * throws "Expected boolean, received string" on submission.
 *
 * `zCheckbox` accepts both shapes (the "on" string from native form data and
 * the boolean from JSON-style API payloads) and returns a boolean | undefined.
 * Chain `.default(true)` / `.default(false)` to coerce absent values.
 *
 * Usage:
 *   rememberMe: zCheckbox.default(false),
 *   terms: zCheckbox,
 *
 * `.default(true)` on a box the UI pre-checks makes UNTICKING it
 * unrepresentable: the box is simply absent from form data either way, so the
 * schema can't tell "left checked" from "unchecked" — `rememberMe` above hit
 * exactly this.
 */
export const zCheckbox = z
  .union([z.boolean(), z.literal("on").transform(() => true)])
  .optional();

/**
 * Standard email field for auth / account-creation forms. Normalizes the value
 * — trims surrounding whitespace, then lowercases — so the address that becomes
 * the SCAPI customer `login` (the account-uniqueness key) is canonical.
 *
 * SFCC-11927: the Workers register + checkout-signup flows sent the raw,
 * as-typed email straight through as `login`. SCAPI's `registerCustomer`
 * duplicate check does NOT collapse case / whitespace the way SFRA's platform
 * `CustomerMgr.createCustomer` does, so `Vija@x.com` and `vija@x.com` (or a
 * stray trailing space) slipped past as two separate accounts sharing one email
 * address. Normalizing here restores parity with the SFRA storefront.
 *
 * Trim runs BEFORE `.email()` so a leading/trailing space doesn't fail
 * validation; lowercase runs last so the parsed value is already canonical for
 * every downstream consumer (login handshake, profile email, cnstrcUserId).
 *
 * Usage:
 *   email: zEmail(EMAIL_ERROR),
 */
export const zEmail = (emailError: string) =>
  z
    .string({ required_error: "This field is required" })
    .trim()
    .min(1, "This field is required")
    .email({ message: emailError })
    .toLowerCase();

/**
 * Wraps a zod schema so blank input skips validation entirely and resolves to
 * `undefined`. HTML forms submit empty inputs as `""`, not `undefined`, so a
 * plain `.optional()` on a regex- or transform-validated field (e.g. phone)
 * still fires "invalid format" when the user leaves it blank. `zOptional`
 * coerces empty / whitespace-only strings to `undefined` before the inner
 * schema runs, so optional fields are truly optional.
 *
 * Use for every form field that the UI labels "(Optional)" — the inner
 * schema's rules only apply once the user has typed something.
 *
 * Usage:
 *   phone: zOptional(phoneValidator),
 *   address2: zOptional(z.string().max(66)),
 */
export const zOptional = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    val =>
      val === null || val === undefined
        ? undefined
        : typeof val === "string" && val.trim() === ""
          ? undefined
          : val,
    schema.optional(),
  );

/**
 * Safe JSON parse for `z.preprocess`: returns `undefined` (which the wrapped
 * schema then rejects, so `safeParse` fails and the caller's fallback wins)
 * instead of throwing. Keeps a schema over a JSON-string column total without a
 * try/catch at every call site.
 *
 * Usage:
 *   z.preprocess(zJson, MyShape.transform(...))
 */
export const zJson = (raw: unknown): unknown => {
  if (typeof raw !== "string") return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};
